import { useLayoutEffect, useRef, useState } from "react";
import Div100vh, { use100vh } from "react-div-100vh";
import { Switch } from "@chakra-ui/react";
import { useMotionValue, useTransform, useSpring } from "framer-motion";
import {
  MotionFlex as Flex,
  MotionBox as Box,
  springAppearBelow,
} from "utils/motion";
import Text from "components/base/Text";
import Trans from "components/base/Trans";
import Slider from "components/base/Slider";
import {
  getMaxDistance,
  getFuel,
  kgsFactor,
  getCurrentGrossWeight,
  getFuelWeight,
  minGrossWeight,
  maxGrossWeight,
  fuelTankMax,
  maxPassengersWeight,
  maxCargoWeight,
  getTime,
} from "utils/mapUtils";
import ArrowIcon from "components/icons/ArrowDown";
import Button from "components/base/Button";

const DecsLine = ({ label, value, ...props }) => (
  <Flex pt="sm" justify="space-between" align="center" {...props}>
    <Text type="p_sm" fontWeight="bold" color="gray.400">
      {label}
    </Text>
    <Text type="p_sm" color="gray.400">
      {value}
    </Text>
  </Flex>
);

const getMassDisplay = (value, massFactor) =>
  `${(value / massFactor).toFixed(0)} ${massFactor === 1 ? "lbs" : "kg"}`;

const MapPanelView = (props) => {
  const ref = useRef();
  const screenHeight = use100vh();
  const [height, setHeight] = useState(-450);
  const motionY = useMotionValue(0);
  const springY = useSpring(motionY, { stiffness: 176, damping: 26 });

  const rotate = useTransform(springY, [height, 0], ["180deg", "0deg"]);
  const backgroundColor = useTransform(
    springY,
    [height, 0],
    ["rgba(0,0,0, 0.6)", "rgba(0,0,0, 0)"],
  );

  const updateMotion = (event, info) => {
    springY.set(info.offset.y > 0 ? 0 : height);
  };

  useLayoutEffect(() => {
    // This will run synchronously after all DOM mutations, but before painting
    if (ref.current && screenHeight) {
      const { height: panelHeight } = ref.current.getBoundingClientRect();
      if (panelHeight > 0) {
        // Cap upward movement so the panel never slides above the top of the screen
        const topPadding = 50;
        const maxUpward = screenHeight - 102 - topPadding;
        const centeredSlide = Math.round(
          screenHeight / 2 + panelHeight / 2 - 102,
        );
        setHeight(-Math.min(centeredSlide, maxUpward));
      }
    }
  }, [screenHeight]);

  return (
    <Div100vh>
      <Flex
        h="100%"
        w={["100%", null, "365px"]}
        position="absolute"
        top={0}
        right={0}
        left={[0, 0, "auto"]}
        mx={[0, 0, 5, 10]}
        align="center"
        style={{ pointerEvents: "none", backgroundColor }}
        zIndex={10}
      >
        <Box
          w="100%"
          px={[5, 5, 5, 10]}
          position="absolute"
          top={screenHeight != null ? screenHeight - 102 : "calc(100% - 102px)"}
          style={{ pointerEvents: "all", y: springY }}
          display={["block", "block", "none"]}
        >
          <Flex
            drag="y"
            ref={ref}
            dragSnapToOrigin
            dragMomentum={false}
            onDragEnd={updateMotion}
            direction="column"
            style={{
              pointerEvents: "all",
              boxShadow: "0 2px 4px rgb(0, 0, 0, 0.3)",
            }}
          >
            <Box
              animate={{ y: ["0px", "-20px", "0px"] }}
              transition={{
                times: [0, 0.4, 1],
                delay: 3,
                duration: 1,
                ease: "easeInOut",
              }}
            >
              <PanelView
                onClick={() =>
                  springY.set(
                    springY.get() >= 0 && springY.get() < 100 ? height : 0,
                  )
                }
                rotate={rotate}
                {...props}
              />
            </Box>
          </Flex>
        </Box>

        {/* DESKTOP */}
        <Flex
          display={["none", "none", "flex"]}
          direction="column"
          w="100%"
          mt="50px"
          style={{ pointerEvents: "all" }}
          variants={springAppearBelow}
          initial="hidden"
          animate="visible"
        >
          <PanelView {...props} />
        </Flex>
      </Flex>
    </Div100vh>
  );
};

const PanelView = ({
  onClick,
  rotate,
  cargo,
  onCargoChange,
  passengers,
  onPassengersChange,
}) => {
  const [massFactor, setMassFactor] = useState(1);
  const grossWeight = minGrossWeight + cargo + passengers;
  const range = getMaxDistance(grossWeight).toFixed(2);
  const fuel = getFuel(grossWeight);
  const currentGrossWeight = getCurrentGrossWeight(grossWeight);
  const fuelWeight = getFuelWeight(grossWeight);

  return (
    <Box>
      <Flex
        bg="rgb(0, 0, 0, 0.7)"
        h="46px"
        px={["sm", "md", "sm"]}
        justify="space-between"
        align="center"
        style={{ cursor: onClick ? "pointer" : "arrow" }}
        onClick={onClick}
      >
        <Text type="title_sm" fontWeight="bold" color="white">
          <Trans id="LBL_RANGE_SIMULATOR_TITLE" />
        </Text>
        <Box mr="-12px" display={["block", "block", "none"]} style={{ rotate }}>
          <ArrowIcon />
        </Box>
      </Flex>
      <Flex
        bg="#272d33"
        px={["sm", "md", "sm"]}
        py="xs"
        justify="space-between"
        align="center"
      >
        <Text type="title_xs" fontWeight="bold" color="white">
          <Trans id="LBL_MAP_CONTROL_HX50_RANGE" />
        </Text>
        <Box>
          <Text type="title_sm" color="green.300" fontWeight="bold">
            {`${range} Nm`}
          </Text>
          <Text
            type="title_xs"
            color="green.300"
            fontWeight="bold"
            textTransform="lowercase"
            letterSpacing={0}
            align="right"
          >
            {getTime(fuel)}
          </Text>
        </Box>
      </Flex>
      <Box px={["sm", "md", "md"]} pb={["md", "md", "lg"]} bg="white">
        <Flex
          pt="sm"
          pb="sm"
          borderBottomColor="gray.300"
          borderBottomWidth="1px"
          justify="space-between"
          align="center"
        >
          <Text type="title_xs" color="black.700">
            <Trans id="LBL_MAP_CONTROL_TITLE" />
          </Text>
          <Flex bg="gray.200" p="5px 10px" borderRadius="23px">
            <Text type="p_sm" fontWeight="bold" color="black.700">
              lbs
            </Text>
            <Switch
              size="md"
              mx="xs"
              onChange={() => setMassFactor(massFactor === 1 ? kgsFactor : 1)}
            />
            <Text type="p_sm" fontWeight="bold" color="black.700">
              kg
            </Text>
          </Flex>
        </Flex>
        <Slider
          label={<Trans id="LBL_MAP_CONTROL_PASSENGERS" />}
          valueLabel={getMassDisplay(passengers, massFactor)}
          value={passengers}
          onChange={(value) =>
            onPassengersChange(
              range > 10 || value < passengers ? value : passengers,
            )
          }
          onChangeEnd={(value) =>
            onPassengersChange(range > 10 ? value : value + fuelWeight - 5)
          }
          min={0}
          max={maxPassengersWeight}
        />
        <Slider
          label={<Trans id="LBL_MAP_CONTROL_CARGO" />}
          valueLabel={getMassDisplay(cargo, massFactor)}
          value={cargo}
          onChange={(value) =>
            onCargoChange(range > 10 || value < cargo ? value : cargo)
          }
          onChangeEnd={(value) =>
            onCargoChange(range > 10 ? value : value + fuelWeight - 5)
          }
          max={maxCargoWeight}
        />
        <Slider
          pt="sm"
          isReadOnly
          label={<Trans id="LBL_MAP_CONTROL_FUEL" />}
          valueLabel={`${fuel.toFixed(0)} Gal = ${getMassDisplay(fuelWeight, massFactor)}`}
          value={fuel}
          max={fuelTankMax}
        />
        <Slider
          isReadOnly
          label={<Trans id="LBL_MAP_CONTROL_GROSS_WEIGHT" />}
          valueLabel={getMassDisplay(currentGrossWeight, massFactor)}
          value={currentGrossWeight}
          min={minGrossWeight}
          max={maxGrossWeight}
        />
        <DecsLine
          mt="-18px"
          label=""
          value={
            <Trans
              id="LBL_MAP_CONTROL_MAX_VALUE"
              values={{ value: getMassDisplay(maxGrossWeight, massFactor) }}
            />
          }
          mb="xxs"
        />
        <DecsLine
          label={<Trans id="LBL_MAP_CONTROL_CRUISE_SPEED" />}
          value="140 Kts"
        />
        <DecsLine
          label={<Trans id="LBL_MAP_CONTROL_FUEL_CONSUMPTION" />}
          value="35 Gal / h"
        />
      </Box>
      <Box bg="black.900" py="xs" px={["sm", "md", "sm"]}>
        <Button
          variant="outline"
          colorScheme="white"
          w="100%"
          href="/schedule"
          trackLabel="map_im_interested"
        >
          <Trans id="LBL_CTA_IMINTERESTED" />
        </Button>
      </Box>
    </Box>
  );
};

export default MapPanelView;
